import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Moon, CalendarDays, Sun, Clock } from 'lucide-react';
import { remindersAPI } from '../utils/api';
import { useI18n } from '../contexts/I18nContext';
import { formatHijriFromStored, formatHijriDateDetailed, formatGregorianDateShort, formatPersianFromStored, getNextOccurrence } from '../utils/dateHelpers';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';

/**
 * Returns the most recent PAST occurrence within the last 30 days,
 * correctly handling all frequencies (once / weekly / monthly / yearly)
 * and all calendars (Gregorian, Hijri, Persian).
 */
const getRecentDate = (reminder) => {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const thirtyDaysAgo = new Date(now); thirtyDaysAgo.setDate(now.getDate() - 30);
  const endOfToday = new Date(now); endOfToday.setHours(23, 59, 59, 999);

  const remDate = new Date(reminder.date);
  const freq = reminder.repeatFrequency || (reminder.repeatYearly ? 'yearly' : 'once');

  // ── One-time: raw date only ──────────────────────────────────────────────
  if (freq === 'once') {
    return (remDate >= thirtyDaysAgo && remDate <= endOfToday) ? remDate : null;
  }

  // ── Weekly: most recent past occurrence of the same weekday (always 0–6 days ago)
  if (freq === 'weekly') {
    const daysSince = (now.getDay() - remDate.getDay() + 7) % 7;
    const last = new Date(now); last.setDate(now.getDate() - daysSince);
    return last >= thirtyDaysAgo ? last : null;
  }

  // ── Monthly / Yearly: use getNextOccurrence to find today's (or upcoming)
  //    occurrence, then derive the most recent past one.
  if (freq === 'monthly' || freq === 'yearly') {
    const next = getNextOccurrence(reminder.date, reminder.repeatYearly, reminder.hijriDate, reminder.calendarType, reminder.persianDate, freq);
    next.setHours(0, 0, 0, 0);

    // If the next occurrence is today, it's also the recent occurrence
    if (next.getTime() === now.getTime()) return now;

    // Next occurrence is in the future — the most recent PAST occurrence is one period back
    const prev = new Date(next);
    if (freq === 'yearly') {
      prev.setFullYear(prev.getFullYear() - 1);
    } else {
      // Monthly: subtract one approximate calendar month
      // Hijri ≈ 29.5 days, Persian ≈ 30.4 days, Gregorian varies
      const approxDays = (reminder.calendarType === 'hijri') ? 29 : 30;
      prev.setDate(prev.getDate() - approxDays);
    }
    prev.setHours(0, 0, 0, 0);
    return (prev >= thirtyDaysAgo && prev <= endOfToday) ? prev : null;
  }

  return null;
};

const formatDaysAgo = (date, t) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / (1000 * 60 * 60 * 24));
  if (diff === 0) return t('relative.today') || 'Today';
  if (diff === 1) return t('relative.yesterday') || 'Yesterday';
  return (t('relative.days_ago') || '{n} days ago').replace('{n}', diff);
};

const RecentRemindersPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [recentReminders, setRecentReminders] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const all = await remindersAPI.getAll();
        const safeAll = Array.isArray(all) ? all : [];
        const withDates = safeAll
          .map(rem => {
            const recentDate = getRecentDate(rem);
            return recentDate ? { ...rem, _recentDate: recentDate } : null;
          })
          .filter(Boolean)
          .sort((a, b) => b._recentDate - a._recentDate);
        setRecentReminders(withDates);
      } catch (err) {
        console.error('Failed to load recent reminders:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>

          {/* Header */}
          <div className="flex items-center gap-4 mb-2">
            <button onClick={() => navigate('/home')} data-testid="back-button"
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
              <ArrowLeft size={20} className="text-slate-900" />
            </button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="recent-page-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('recent.title')}
              </h1>
            </div>
          </div>

          <p className="text-sm text-slate-500 mb-6 ml-14" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {t('recent.subtitle')}
          </p>

          {/* List */}
          {recentReminders.length === 0 ? (
            <div className="bg-white p-10 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center" data-testid="recent-empty">
              <Clock size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {t('recent.empty') || 'No events in the last 30 days'}
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="recent-reminders-list">
              {recentReminders.map((reminder, index) => {
                const isHijri = reminder.calendarType === 'hijri';
                const isPersian = reminder.calendarType === 'persian';
                return (
                  <motion.div
                    key={reminder.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    data-testid="recent-reminder-item"
                    className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-slate-50 cursor-pointer active:scale-[0.99] transition-transform"
                    onClick={() => navigate(`/reminder/${reminder.id}`)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                        isPersian ? 'bg-orange-100 text-orange-700' :
                        isHijri ? 'bg-emerald-100 text-emerald-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {isPersian ? <Sun size={24} /> : isHijri ? <Moon size={24} /> : <CalendarDays size={24} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-slate-900 font-semibold text-base line-clamp-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {reminder.title}
                        </h3>
                        <p className="text-slate-500 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>
                          {isPersian && reminder.persianDate
                            ? formatPersianFromStored(reminder.persianDate, lang)
                            : isHijri && reminder.hijriDate
                            ? formatHijriFromStored(reminder.hijriDate, lang)
                            : isHijri
                            ? formatHijriDateDetailed(new Date(reminder.date), lang)
                            : formatGregorianDateShort(new Date(reminder.date), lang)
                          }
                        </p>
                        {reminder.description && (
                          <p className="text-slate-400 text-xs mt-1 line-clamp-1">{reminder.description}</p>
                        )}
                      </div>
                      {/* Days ago badge */}
                      <div className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${
                        isPersian ? 'bg-orange-50 text-orange-600' :
                        isHijri ? 'bg-emerald-50 text-emerald-600' :
                        'bg-blue-50 text-blue-600'
                      }`} data-testid="days-ago-badge">
                        {formatDaysAgo(reminder._recentDate, t)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
      <BottomNav />
    </div>
  );
};

export default RecentRemindersPage;
